import type { DifficultyId } from './types';

/* All tuning numbers live here so balancing never means hunting through logic. */

export interface Difficulty {
  id: DifficultyId;
  label: string;
  blurb: string;
  /** Real seconds per in-game hour (the shift is 8 hours). */
  secondsPerHour: number;
  patienceMult: number;
  /** Multiplies every cook/burn threshold. >1 = more forgiving. */
  cookSlack: number;
  chairWindowMult: number;
  chairFlightMult: number;
  spawnMult: number;
  maxTicketsBonus: number;
  chairsPerFight: readonly [number, number];
  indexLossMult: number;
  secondFightChance: number;
}

export const DIFFICULTIES: Record<DifficultyId, Difficulty> = {
  trainee: {
    id: 'trainee',
    label: 'TRAINEE',
    blurb: 'Slower grill, patient customers, one chair per fight.',
    secondsPerHour: 50,
    patienceMult: 1.45,
    cookSlack: 1.3,
    chairWindowMult: 1.45,
    chairFlightMult: 1.2,
    spawnMult: 0.8,
    maxTicketsBonus: -1,
    chairsPerFight: [1, 1],
    indexLossMult: 0.7,
    secondFightChance: 0,
  },
  standard: {
    id: 'standard',
    label: 'GRILL OPERATOR',
    blurb: 'The real graveyard shift. Daily shifts use this.',
    secondsPerHour: 45,
    patienceMult: 1,
    cookSlack: 1,
    chairWindowMult: 1,
    chairFlightMult: 1,
    spawnMult: 1,
    maxTicketsBonus: 0,
    chairsPerFight: [1, 2],
    indexLossMult: 1,
    secondFightChance: 0.35,
  },
  boss: {
    id: 'boss',
    label: '3AM FINAL BOSS',
    blurb: 'Impatient crowd, faster burns, chairs everywhere.',
    secondsPerHour: 45,
    patienceMult: 0.78,
    cookSlack: 0.88,
    chairWindowMult: 0.8,
    chairFlightMult: 0.88,
    spawnMult: 1.2,
    maxTicketsBonus: 1,
    chairsPerFight: [2, 3],
    indexLossMult: 1.2,
    secondFightChance: 1,
  },
};

export const COOK = {
  hashBrowns: { golden: 5, crispy: 9, burnt: 13.5 },
  waffle: { ready: 6, burnt: 12.5 },
  eggs: 2.6,
  bacon: 2.2,
  smokeAlarmAfter: 6,
} as const;

export const FLOW = {
  walkIn: 1.3,
  settle: 0.5,
  eat: 4,
  leave: 1.3,
  usualReveal: 3,
  /** Patience seconds = base + perItem × items, then archetype & difficulty multipliers. */
  patienceBase: 31,
  patiencePerItem: 9,
  /** Early-night grace so first-timers can learn the lingo. */
  firstHourPatienceBonus: 1.35,
  coffeeDrainPerSecond: 1 / 26,
  coffeeLowThreshold: 0.3,
  coffeeEmptyPatienceMult: 1.5,
  coffeeOnlyVisit: [16, 24] as const,
  idleSelfServeAfter: 24,
} as const;

export const CHAIR = {
  flight: 1.55,
  window: 0.45,
  perfectBeforeImpact: 0.17,
  perfectTolerance: 0.075,
  grace: 0.3,
  /** Kitchen & customers slow to this fraction of real time while a chair is airborne. */
  slowmo: 0.22,
  /** Seconds the freeze-frame / crash lingers before play resumes. */
  resolveHold: 0.85,
} as const;

export const INDEX = {
  max: 100,
  /*
   * True to the real FEMA metric: GREEN = full menu, YELLOW = limited menu,
   * RED = closed. Red only happens at zero, and it ends your shift.
   */
  greenAt: 50,
  criticalAt: 20,
  walkout: -12,
  badOrder: -5,
  okOrder: 0,
  goodOrder: 1,
  perfectOrder: 3,
  burnt: -2,
  alarm: -4,
  chairHit: -8,
  hostPleased: 10,
  hostTimeout: -6,
  /** The Waffle House notices only cooks who are keeping it together. */
  hostRequires: 70,
} as const;

export const PRICES = {
  hashBrowns: 3.6,
  topping: 0.55,
  allTheWay: 7.85,
  waffle: { classic: 4.25, pecan: 4.95 },
  eggs: 2.75,
  eggSurcharge: 0.5,
  bacon: 3.4,
} as const;

export const TIPS = {
  baseRate: 0.1,
  qualityRate: 0.18,
  speedRate: 0.12,
  comboStep: 0.35,
  comboCap: 10,
  unbotheredMult: 2,
  liveMult: 1.5,
  chairCatch: 5,
  perfectCatch: 10,
  femaGreen: 25,
  hostPleased: 33.33,
  hostTolerant: 3.33,
  proposalYes: 20,
  coffee: [0.5, 1.25] as const,
} as const;

export interface HourProfile {
  spawnEvery: number;
  maxTickets: number;
  items: readonly (readonly [number, number])[];
  toppings: readonly (readonly [number, number])[];
  allTheWay: number;
  coffeeOnly: number;
}

/** One profile per shift hour: 10P, 11P, 12A, 1A, 2A, 3A, 4A, 5A. */
export const HOURS: readonly HourProfile[] = [
  {
    spawnEvery: 10,
    maxTickets: 2,
    items: [
      [1, 70],
      [2, 30],
    ],
    toppings: [
      [0, 25],
      [1, 45],
      [2, 30],
    ],
    allTheWay: 0,
    coffeeOnly: 0.08,
  },
  {
    spawnEvery: 8.5,
    maxTickets: 3,
    items: [
      [1, 45],
      [2, 42],
      [3, 13],
    ],
    toppings: [
      [0, 15],
      [1, 35],
      [2, 38],
      [3, 12],
    ],
    allTheWay: 0,
    coffeeOnly: 0.1,
  },
  {
    spawnEvery: 7.5,
    maxTickets: 3,
    items: [
      [1, 33],
      [2, 45],
      [3, 22],
    ],
    toppings: [
      [0, 10],
      [1, 26],
      [2, 40],
      [3, 19],
      [4, 5],
    ],
    allTheWay: 0.02,
    coffeeOnly: 0.1,
  },
  {
    spawnEvery: 6.2,
    maxTickets: 4,
    items: [
      [1, 28],
      [2, 42],
      [3, 24],
      [4, 6],
    ],
    toppings: [
      [0, 8],
      [1, 22],
      [2, 38],
      [3, 24],
      [4, 8],
    ],
    allTheWay: 0.03,
    coffeeOnly: 0.08,
  },
  {
    spawnEvery: 4.8,
    maxTickets: 5,
    items: [
      [1, 24],
      [2, 40],
      [3, 26],
      [4, 10],
    ],
    toppings: [
      [0, 8],
      [1, 20],
      [2, 36],
      [3, 26],
      [4, 10],
    ],
    allTheWay: 0.04,
    coffeeOnly: 0.06,
  },
  {
    spawnEvery: 5.0,
    maxTickets: 5,
    items: [
      [1, 24],
      [2, 38],
      [3, 26],
      [4, 12],
    ],
    toppings: [
      [0, 6],
      [1, 18],
      [2, 36],
      [3, 28],
      [4, 12],
    ],
    allTheWay: 0.05,
    coffeeOnly: 0.06,
  },
  {
    spawnEvery: 7.2,
    maxTickets: 4,
    items: [
      [1, 30],
      [2, 40],
      [3, 22],
      [4, 8],
    ],
    toppings: [
      [0, 10],
      [1, 24],
      [2, 36],
      [3, 22],
      [4, 8],
    ],
    allTheWay: 0.03,
    coffeeOnly: 0.12,
  },
  {
    spawnEvery: 7.8,
    maxTickets: 4,
    items: [
      [1, 30],
      [2, 40],
      [3, 22],
      [4, 8],
    ],
    toppings: [
      [0, 12],
      [1, 26],
      [2, 36],
      [3, 20],
      [4, 6],
    ],
    allTheWay: 0.02,
    coffeeOnly: 0.12,
  },
];

export const STATIONS = {
  grillSlots: 4,
  waffleIrons: 2,
  maxTicketsHard: 5,
} as const;

export const HURRICANE_NAMES = [
  'CHAD',
  'KAREN',
  'BRAYDEN',
  'DEBRA',
  'KYLE',
  'TAMMY',
  'DALE',
  'BRENDA',
  'CLETUS',
  'MADISON',
  'JAXON',
  'DOLORES',
] as const;
